<!-- Make sure to match the responsive image ratio sizes with the svg ratio, otherwise this won't work! -->

<template>
  <article v-if="image || hasVideo" class="overflow-hidden">
    <div class="relative bg-black mask-visual" :class="`mask-visual--${shapeNumber}`">
      <slot />
      <MoleculePicture
        v-if="image"
        :image="image"
        :alt="imageIsDecorative ? '' : label"
        :aria-hidden="imageIsDecorative"
        :lazy="false"
        class="block w-full"
        :class="{ 'md:hidden': hasVideo }"
      ></MoleculePicture>
    </div>
  </article>
</template>

<script setup lang="ts">
defineProps({
  image: { type: Object as PropType<Record<string, any>>, required: false, default: undefined },
  imageIsDecorative: { type: Boolean, required: false, default: false },
  hasVideo: { type: Boolean, required: false, default: false },
  shapeNumber: { type: Number, required: false, default: 1, validator: (value: number) => [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(value) },
  label: { type: String, required: false, default: undefined },
  role: { type: String, required: false, default: undefined },
})
</script>

<style lang="scss">
.mask-visual {
  mask-position: bottom;
  mask-repeat: no-repeat;
  mask-size: cover;
  width: calc(100vw + 3px); // correction to fit screen
}

// used for mobile header, e.g. on detail pages
.mask-visual--1 {
  mask-image: url('/images/mask/MaskVisual1.svg');
}

// used for header, e.g. on the home page
.mask-visual--2 {
  mask-image: url('/images/mask/MaskVisual2.svg');
  height: 75vh;

  picture {
    @apply h-full;
  }

  &::after {
    @apply block absolute left-0 top-0 w-full h-full bg-no-repeat z-10;
    content: '';
    background-image: linear-gradient(108.01deg, theme('colors.app.black--70') 15.31%, theme('colors.app.black--0') 40.17%);
  }
}

// used for footers, e.g. on the home page newsletter section
.mask-visual--3 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual3.svg');

  &::after {
    @apply block absolute left-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(128.46deg, theme('colors.app.secondary-9-green--sherpa-blue--80') 29.04%, theme('colors.app.secondary-9-green--sherpa-blue--0') 55.86%);
  }
}

// used for mobile header, e.g. on the home page
.mask-visual--4 {
  @apply -mt-px;
  mask-image: url('/images/mask/MaskVisual4.svg');

  &::after {
    @apply block absolute left-0 top-0 w-full h-full bg-no-repeat -mt-px;
    content: '';
    background-image: linear-gradient(108.01deg, theme('colors.app.black--70') 15.31%, theme('colors.app.black--0') 40.17%);
  }
}

// used for mobile footers, e.g. on the home page newsletter section
.mask-visual--5 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual5.svg');

  &::after {
    @apply block absolute left-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(128.46deg, theme('colors.app.secondary-9-green--sherpa-blue--80') 29.04%, theme('colors.app.secondary-9-green--sherpa-blue--0') 55.86%);
  }
}

// used for the desktop header on the discovery (search) overview and collection detail page
.mask-visual--6 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual6.svg');
  width: 75%;
  margin-left: auto;
  mask-position: right;
  mask-size: contain;

  &::after {
    @apply block absolute right-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(0deg, theme('colors.app.secondary-2-orange--mango-tango') 0%, theme('colors.app.secondary-9-green--sherpa-blue--0') 50%);
  }
}

// used for the mobile header on the discovery pages
.mask-visual--7 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual7.svg');
  mask-position: left;

  &::after {
    @apply block absolute right-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(0deg, theme('colors.app.secondary-2-orange--mango-tango') 0%, theme('colors.app.secondary-9-green--sherpa-blue--0') 50%);
  }
}

// used for the desktop header on the discovery detail page (this is a variant of mask-visual--2)
.mask-visual--8 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual2.svg');

  &::after {
    @apply block absolute right-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(0deg, theme('colors.app.secondary-2-orange--mango-tango') 0%, theme('colors.app.secondary-9-green--sherpa-blue--0') 90%);
  }
}

// used for desktop header on the discovery detail page
.mask-visual--9 {
  @apply -mb-px;
  mask-image: url('/images/mask/MaskVisual9.svg');
  width: 75%;
  margin-left: auto;

  &::after {
    @apply block absolute right-0 top-0 w-full h-full bg-no-repeat -mb-px;
    content: '';
    background: linear-gradient(45deg, theme('colors.app.secondary-2-orange--mango-tango') 0%, theme('colors.app.secondary-9-green--sherpa-blue--0') 50%);
  }
}
</style>
